import { useEffect, useRef, useState } from "react";
import type { UseFieldArrayReplace, UseFormReturn } from "react-hook-form";
import { useDispatch } from "react-redux";

import { App_Url_New } from "@/shared/common/static";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { setMasterCoverage } from "@/shared/redux/reducers/masterCoverageSlice";
import type { InputOption } from "@/shared/types";
import { TRAVEL_QUOTE_STEP_KEYS } from "../common/static";
import { defaultTravelDetailsFormValues } from "../redux/travel-quote/travelQuoteSlice";
import type { TravelDetailsFormValues } from "../redux/type";
import { addDays, formatIsoDate, mapCountryOptions } from "../utils/travelDetails";

type UseTravelDetailsFormEffectsParams = {
  defaultValues?: Partial<TravelDetailsFormValues>;
  form: UseFormReturn<TravelDetailsFormValues>;
  defaultTravellerName: string;
  primaryTravellerName?: string;
  selectedCoverage?: { id?: string | number; label?: string };
  masterCoverageLength: number;
  isUaeInbound: boolean;
  isWorldwideCoverage: boolean;
  coverType: TravelDetailsFormValues["coverType"];
  fieldsLength: number;
  planType: TravelDetailsFormValues["planType"];
  startDate?: string;
  replace: UseFieldArrayReplace<TravelDetailsFormValues, "travellers">;
};

export function useTravelDetailsFormEffects({
  defaultValues,
  form,
  defaultTravellerName,
  primaryTravellerName,
  selectedCoverage,
  masterCoverageLength,
  isUaeInbound,
  isWorldwideCoverage,
  coverType,
  fieldsLength,
  planType,
  startDate,
  replace,
}: UseTravelDetailsFormEffectsParams) {
  const dispatch = useDispatch();
  const [countryOptions, setCountryOptions] = useState<InputOption<string>[]>([]);
  const [shouldShowCountryFields, setShouldShowCountryFields] = useState(false);
  const previousCoverageIdRef = useRef<string | undefined>(undefined);
  const todayIso = formatIsoDate(new Date());

  useEffect(() => {
    form.reset({
      ...defaultTravelDetailsFormValues,
      ...defaultValues,
    });
  }, [defaultValues, form]);

  useEffect(() => {
    if (!form.getValues("startDate")) {
      form.setValue("startDate", todayIso, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, todayIso]);

  useEffect(() => {
    if (!defaultTravellerName || primaryTravellerName?.trim()) {
      return;
    }

    form.setValue("travellers.0.name", defaultTravellerName, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [defaultTravellerName, form, primaryTravellerName]);

  useEffect(() => {
    const getMasterCoverage = async () => {
      try {
        const response = await axiosInstance.get(App_Url_New.ApiEndPoint.masterApi);

        dispatch(setMasterCoverage(response?.data || []));
      } catch (error) {
        console.error("Error fetching master coverage:", error);
      }
    };

    if (!masterCoverageLength) {
      getMasterCoverage();
    }
  }, [dispatch, masterCoverageLength]);

  useEffect(() => {
    if (!selectedCoverage?.label) {
      return;
    }

    const currentDetails = form.getValues();

    if (currentDetails.coverageLabel !== selectedCoverage.label) {
      form.setValue("coverageLabel", selectedCoverage.label, {
        shouldDirty: true,
        shouldValidate: true,
      });

      dispatch({
        type: TRAVEL_QUOTE_STEP_KEYS.TRAVEL_DETAILS,
        payload: {
          stepName: "travelDetails",
          data: {
            ...currentDetails,
            coverageLabel: selectedCoverage.label,
          },
        },
      });
    }
  }, [dispatch, form, selectedCoverage]);

  useEffect(() => {
    if (selectedCoverage?.id === undefined) {
      return;
    }

    const coverageId = String(selectedCoverage.id);

    // Keep restored values on the initial load, but never retain countries
    // selected for a different coverage after the user changes it.
    if (
      previousCoverageIdRef.current !== undefined &&
      previousCoverageIdRef.current !== coverageId
    ) {
      form.setValue("fromCountry", "", {
        shouldDirty: true,
        shouldValidate: false,
      });
      form.setValue("destination", "", {
        shouldDirty: true,
        shouldValidate: false,
      });
      form.clearErrors(["fromCountry", "destination"]);
    }

    previousCoverageIdRef.current = coverageId;
  }, [form, selectedCoverage?.id]);

  useEffect(() => {
    setShouldShowCountryFields(false);
    setCountryOptions([]);

    if (!selectedCoverage?.id) {
      return;
    }

    const getCountries = async () => {
      try {
        const response = await axiosInstance.post(App_Url_New.ApiEndPoint.countryApi, {
          id: selectedCoverage.id,
          is_active: true,
          order_by: "asc",
        });

        if (response?.data) {
          setCountryOptions(mapCountryOptions(response.data));
          setShouldShowCountryFields(true);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        setShouldShowCountryFields(false);
      }
    };

    getCountries();
  }, [selectedCoverage]);

  useEffect(() => {
    if (!shouldShowCountryFields) {
      return;
    }

    const currentFromCountry = form.getValues("fromCountry");
    const currentDestination = form.getValues("destination");

    if (isUaeInbound) {
      // UAE Inbound requires a non-UAE departure. Preserve a restored or
      // user-selected country when returning to this step; only clear the
      // outbound default left behind after switching coverage.
      if (currentFromCountry === "United Arab Emirates") {
        form.setValue("fromCountry", "", {
          shouldDirty: true,
          shouldValidate: false,
        });
      }

      form.setValue("destination", "United Arab Emirates", {
        shouldDirty: true,
        shouldValidate: false,
      });

      form.clearErrors(["fromCountry", "destination"]);

      return;
    }

    if (isWorldwideCoverage) {
      form.setValue("fromCountry", "United Arab Emirates", {
        shouldDirty: true,
        shouldValidate: true,
      });
      if (!currentDestination || currentDestination === "United Arab Emirates") {
        form.setValue("destination", "", {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
      return;
    }

    if (
      currentFromCountry === "United Arab Emirates" &&
      currentDestination === "United Arab Emirates"
    ) {
      form.setValue("destination", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, isUaeInbound, isWorldwideCoverage, shouldShowCountryFields, countryOptions]);
  useEffect(() => {
    if (coverType === "individual" && fieldsLength > 1) {
      replace([
        form.getValues("travellers.0") || {
          name: "",
          dob: "",
        },
      ]);
    }
  }, [coverType, fieldsLength, form, replace]);

  useEffect(() => {
    if (planType === "single") {
      const currentEndDate = form.getValues("endDate");

      if (currentEndDate && startDate) {
        const end = new Date(currentEndDate);
        const start = new Date(startDate);
        const maxEnd = addDays(start, 179);

        if (Number.isNaN(end.getTime()) || end < start || end > maxEnd) {
          form.setValue("endDate", "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      } else if (!startDate && currentEndDate) {
        form.setValue("endDate", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      return;
    }

    if (!startDate) {
      form.setValue("endDate", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    const start = new Date(startDate);

    if (Number.isNaN(start.getTime())) {
      return;
    }

    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);

    form.setValue("endDate", formatIsoDate(end), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form, planType, startDate]);

  return {
    countryOptions,
    showCountryFields: shouldShowCountryFields,
    todayIso,
  };
}
